import React from "react";
import { Link } from "react-router-dom";
import Tag from "./Tag";
import "./ProductCard.css";

/**
 * ProductCard - Premium card for displaying products
 * Designed with a luxury, expensive aesthetic
 */
const ProductCard = ({
  name,
  tagline,
  description,
  status = "Available",
  path,
  slug,
  category,
  featured,
  new: isNew,
  icon,
  color,
  license,
  tags,
  openSource,
  githubUrl,
  className = "",
}) => {
  // Format product name to title case for display
  const displayName = name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <div
      className={`product-card ${featured ? "product-card--featured" : ""} ${isNew ? "product-card--new" : ""} ${className}`}
    >
      {/* Header with product ID and badges */}
      <div className="product-card__header">
        <div className="product-card__meta">
          <span className="product-card__number">{displayName}</span>
        </div>
        <div className="product-card__badges">
          {isNew && (
            <span className="product-card__badge product-card__badge--new">
              New
            </span>
          )}
          {featured && (
            <span className="product-card__badge product-card__badge--featured">
              Featured
            </span>
          )}
          {openSource && (
            <span className="product-card__badge product-card__badge--opensource">
              Open Source
            </span>
          )}
        </div>
      </div>

      {/* Icon area */}
      <div
        className="product-card__icon-area"
        style={{ borderColor: color ? `${color}20` : undefined }}
      >
        <span
          className="product-card__icon"
          style={{ color: color || undefined }}
        >
          {getIcon(icon)}
        </span>
      </div>

      {/* Tagline */}
      <h3 className="product-card__tagline">{tagline}</h3>

      {/* Description */}
      <p className="product-card__description">{description}</p>

      {/* License info - One-time purchase */}
      {license && (
        <div className="product-card__license">
          <span className="product-card__license-icon">◆</span>
          <span className="product-card__license-text">{license}</span>
        </div>
      )}

      {/* Tags */}
      {tags && tags.length > 0 && (
        <div className="product-card__tags">
          {tags.slice(0, 3).map((tag, index) => (
            <Tag key={index} text={tag} size="xs" />
          ))}
          {tags.length > 3 && (
            <span className="product-card__tag-more">+{tags.length - 3}</span>
          )}
        </div>
      )}

      {/* CTA Button */}
      {(path || githubUrl) && (
        <div className="product-card__cta">
          {path && (
            <Link to={path} className="product-card__link">
              <button
                className={`product-card__button ${featured ? "product-card__button--featured" : ""}`}
              >
                <span>{featured ? "Explore" : "Learn more"}</span>
                <span className="arrow-icon">→</span>
              </button>
            </Link>
          )}
          {githubUrl && (
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="product-card__github-link"
            >
              <button className="product-card__button product-card__button--secondary">
                <span>GitHub</span>
                <span className="arrow-icon">→</span>
              </button>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// Professional icon mapping
const getIcon = (iconName) => {
  const icons = {
    shield: "◆",
    "shield-alt": "◆",
    cog: "◈",
    "check-square": "▣",
    bolt: "◉",
    "project-diagram": "◊",
    stream: "◎",
    "clipboard-check": "▤",
    lock: "◐",
    link: "◑",
    clock: "◓",
    database: "◈",
    server: "◆",
    network: "◎",
    fingerprint: "◊",
    vault: "▣",
    mic: "◈",
    brain: "◉",
  };
  return icons[iconName] || "◆";
};

export default ProductCard;
